/**
 * Simple API cache service to minimize redundant backend calls
 */

type CacheExpiration = number | null; // Timestamp or null for never expire

interface CacheItem<T> {
  data: T;
  expiration: CacheExpiration;
}

class ApiCache {
  private cache: Map<string, CacheItem<any>> = new Map();
  
  /**
   * Get data from cache
   * @param key Cache key
   * @returns Cached data or null if not found or expired
   */
  get<T>(key: string): T | null {
    const item = this.cache.get(key);
    
    if (!item) {
      return null;
    }
    
    // Check if expired
    if (item.expiration && item.expiration < Date.now()) {
      this.cache.delete(key);
      return null;
    }
    
    return item.data;
  }
  
  /**
   * Set data in cache
   * @param key Cache key
   * @param data Data to cache
   * @param ttlMs Time to live in milliseconds (default: 30 seconds)
   */
  set<T>(key: string, data: T, ttlMs: number = 30000): void {
    // Calculate expiration time
    const expiration = ttlMs ? Date.now() + ttlMs : null;
    
    this.cache.set(key, {
      data,
      expiration
    });
  }
  
  /**
   * Remove item from cache
   * @param key Cache key
   */
  remove(key: string): void {
    this.cache.delete(key);
  }
  
  /**
   * Clear all cached data
   */
  clear(): void {
    this.cache.clear();
  }
  
  /**
   * Check if key exists in cache and is not expired
   * @param key Cache key
   * @returns Whether valid data exists in cache
   */
  has(key: string): boolean {
    const item = this.cache.get(key);
    
    if (!item) {
      return false;
    }
    
    if (item.expiration && item.expiration < Date.now()) {
      this.cache.delete(key);
      return false;
    }
    
    return true;
  }
  
  /**
   * Wrap an async function with caching
   * @param key Cache key
   * @param fetchFn Function to call if cache miss
   * @param ttlMs Cache duration in milliseconds (default: 30 seconds)
   * @returns Result from cache or from fetchFn
   */
  async getOrFetch<T>(key: string, fetchFn: () => Promise<T>, ttlMs: number = 30000): Promise<T> {
    // Check cache first
    const cachedData = this.get<T>(key);
    if (cachedData !== null) {
      return cachedData;
    }
    
    // Cache miss, fetch data
    const data = await fetchFn();
    
    // Store in cache
    this.set(key, data, ttlMs);
    
    return data;
  }
}

// Export singleton instance
export const apiCache = new ApiCache();